import React, { useEffect, useMemo, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';
import useIsMobile from '../hooks/useIsMobile';

const emptyProfileForm = {
  profile_code: '',
  profile_name: '',
  payer_type: 'SELF',
  owner_entity_type: 'PROPERTY',
  owner_entity_id: '',
  billing_company_name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  country: 'India',
  pin_code: '',
  gstin_number: '',
  phone: '',
  email: '',
  website: '',
  logo_url: '',
  is_default: false,
  is_active: true
};

const emptyFilters = {
  payer_type: '',
  owner_entity_type: '',
  owner_entity_id: '',
  include_inactive: true
};

const defaultInvoiceConfig = {
  template: 'INV-{YYYY}{MM}-{SEQ}',
  seq_padding: 5,
  reset_cycle: 'NEVER',
  is_active: true,
  is_configured: false,
  current_seq: 0,
  last_reset_key: null
};

const defaultReservationConfig = {
  template: 'RES-{HOTELCODE}-{SEQ}',
  seq_padding: 6,
  reset_cycle: 'NEVER',
  is_active: true,
  is_configured: false,
  current_seq: 0,
  last_reset_key: null
};

const toUpper = (value) => String(value || '').trim().toUpperCase();

const deriveDefaultOwnerType = (payerType) => {
  if (payerType === 'CORPORATE') return 'CORPORATE';
  if (payerType === 'TRAVEL_AGENT') return 'TRAVEL_AGENT';
  return 'PROPERTY';
};

function PmsBillingInfoMaster({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyProfileForm);
  const [editingId, setEditingId] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [invoiceConfig, setInvoiceConfig] = useState(defaultInvoiceConfig);
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceContinueFrom, setInvoiceContinueFrom] = useState('');
  const [reservationConfig, setReservationConfig] = useState(defaultReservationConfig);
  const [reservationSaving, setReservationSaving] = useState(false);
  const [reservationContinueFrom, setReservationContinueFrom] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [corporates, setCorporates] = useState([]);
  const [travelAgents, setTravelAgents] = useState([]);
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const ownerOptions = useMemo(() => {
    const ownerType = String(form.owner_entity_type || '').toUpperCase();
    if (ownerType === 'CORPORATE') {
      return (Array.isArray(corporates) ? corporates : []).map((row) => ({
        id: row.corporate_id,
        label: `${row.corporate_code || `CORP-${row.corporate_id}`} - ${row.corporate_name || 'Corporate'}`
      }));
    }
    if (ownerType === 'TRAVEL_AGENT') {
      return (Array.isArray(travelAgents) ? travelAgents : []).map((row) => ({
        id: row.travel_agent_id,
        label: `${row.agent_code || `TA-${row.travel_agent_id}`} - ${row.agent_name || 'Travel Agent'}`
      }));
    }
    return [];
  }, [corporates, travelAgents, form.owner_entity_type]);

  const loadProfiles = async (filterState = filters) => {
    if (!selectedProperty?.id) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const q = new URLSearchParams();
      if (filterState.include_inactive) q.set('all', '1');
      if (filterState.payer_type) q.set('payer_type', filterState.payer_type);
      if (filterState.owner_entity_type) q.set('owner_entity_type', filterState.owner_entity_type);
      if (String(filterState.owner_entity_id || '').trim()) q.set('owner_entity_id', String(filterState.owner_entity_id).trim());
      const qs = q.toString();
      const res = await fetch(`/api/v1/pms/billing-profiles${qs ? `?${qs}` : ''}`, { headers: headers() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(data?.error || 'Failed to load billing profiles');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load billing profiles');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoiceConfig = async () => {
    if (!selectedProperty?.id) {
      setInvoiceConfig(defaultInvoiceConfig);
      return;
    }
    try {
      const res = await fetch('/api/v1/pms/invoice-number-config', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load invoice number config');
      setInvoiceConfig({
        template: data?.template || defaultInvoiceConfig.template,
        seq_padding: Number(data?.seq_padding || defaultInvoiceConfig.seq_padding),
        reset_cycle: data?.reset_cycle || defaultInvoiceConfig.reset_cycle,
        is_active: data?.is_active !== false,
        is_configured: data?.is_configured === true,
        current_seq: Number(data?.current_seq || 0),
        last_reset_key: data?.last_reset_key || null
      });
    } catch (err) {
      setError(err?.message || 'Failed to load invoice number config');
    }
  };

  const loadReservationConfig = async () => {
    if (!selectedProperty?.id) {
      setReservationConfig(defaultReservationConfig);
      return;
    }
    try {
      const res = await fetch('/api/v1/pms/reservation-number-config', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load reservation number config');
      setReservationConfig({
        template: data?.template || defaultReservationConfig.template,
        seq_padding: Number(data?.seq_padding || defaultReservationConfig.seq_padding),
        reset_cycle: data?.reset_cycle || defaultReservationConfig.reset_cycle,
        is_active: data?.is_active !== false,
        is_configured: data?.is_configured === true,
        current_seq: Number(data?.current_seq || 0),
        last_reset_key: data?.last_reset_key || null
      });
    } catch (err) {
      setError(err?.message || 'Failed to load reservation number config');
    }
  };

  const loadOwners = async () => {
    if (!selectedProperty?.id) {
      setCorporates([]);
      setTravelAgents([]);
      return;
    }
    try {
      const [corpRes, taRes] = await Promise.all([
        fetch('/api/v1/pms/corporates?all=1', { headers: headers() }),
        fetch('/api/v1/pms/travel-agents?all=1', { headers: headers() })
      ]);
      const corpData = await corpRes.json().catch(() => ([]));
      const taData = await taRes.json().catch(() => ([]));
      setCorporates(corpRes.ok && Array.isArray(corpData) ? corpData : []);
      setTravelAgents(taRes.ok && Array.isArray(taData) ? taData : []);
    } catch (_) {
      setCorporates([]);
      setTravelAgents([]);
    }
  };

  useEffect(() => {
    setError('');
    setMessage('');
    setForm(emptyProfileForm);
    setEditingId('');
    setFilters(emptyFilters);
    setRows([]);
    setInvoiceConfig(defaultInvoiceConfig);
    setReservationConfig(defaultReservationConfig);
    loadOwners();
    loadProfiles(emptyFilters);
    loadInvoiceConfig();
    loadReservationConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const resetProfileForm = () => {
    setEditingId('');
    setForm(emptyProfileForm);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Only image files are allowed for logo');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo file must be under 2 MB');
      return;
    }
    // Bug 8666 — downscale the logo client-side before upload. The server stores
    // logos at 400px, and a large base64 payload can exceed the proxy's request
    // body limit (~1 MB), which caused >1 MB uploads to fail with "Failed to save"
    // even though 2 MB is allowed. Resizing here keeps the upload tiny regardless
    // of the original file size.
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try {
          const MAX = 512;
          let { width, height } = img;
          if (width > MAX || height > MAX) {
            const scale = Math.min(MAX / width, MAX / height);
            width = Math.round(width * scale);
            height = Math.round(height * scale);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          const isPng = file.type === 'image/png';
          const dataUrl = canvas.toDataURL(isPng ? 'image/png' : 'image/jpeg', 0.85);
          setForm((p) => ({ ...p, logo_url: dataUrl }));
          setError('');
        } catch (_) {
          setForm((p) => ({ ...p, logo_url: reader.result }));
        }
      };
      img.onerror = () => setForm((p) => ({ ...p, logo_url: reader.result }));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    const ownerType = toUpper(form.owner_entity_type || deriveDefaultOwnerType(toUpper(form.payer_type)));
    const ownerIdRaw = String(form.owner_entity_id || '').trim();
    if (!String(form.profile_code || '').trim()) return setError('Profile Code is required');
    if (!String(form.profile_name || '').trim()) return setError('Profile Name is required');
    if (ownerType !== 'PROPERTY' && !ownerIdRaw) return setError(`Owner is required for ${ownerType}`);
    setError('');
    setMessage('');
    try {
      const payload = {
        profile_code: toUpper(form.profile_code),
        profile_name: String(form.profile_name || '').trim(),
        payer_type: toUpper(form.payer_type || 'SELF'),
        owner_entity_type: ownerType,
        owner_entity_id: ownerType === 'PROPERTY' ? null : Number(ownerIdRaw),
        billing_company_name: String(form.billing_company_name || '').trim(),
        address_line1: String(form.address_line1 || '').trim(),
        address_line2: String(form.address_line2 || '').trim(),
        city: String(form.city || '').trim(),
        state: String(form.state || '').trim(),
        country: String(form.country || '').trim(),
        pin_code: String(form.pin_code || '').trim(),
        gstin_number: toUpper(form.gstin_number),
        phone: String(form.phone || '').trim(),
        email: String(form.email || '').trim(),
        website: String(form.website || '').trim(),
        logo_url: form.logo_url && form.logo_url.startsWith('data:') ? form.logo_url : undefined,
        is_default: form.is_default === true,
        is_active: form.is_active !== false
      };
      // On edit: if logo was removed, send explicit null
      if (editingId && !form.logo_url) {
        payload.logo_url = null;
      }
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `/api/v1/pms/billing-profiles/${editingId}` : '/api/v1/pms/billing-profiles';
      const res = await fetch(url, {
        method,
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save billing profile');
      setMessage(`Billing profile ${editingId ? 'updated' : 'created'} successfully`);
      resetProfileForm();
      loadProfiles();
    } catch (err) {
      setError(err?.message || 'Failed to save billing profile');
    }
  };

  const onEditProfile = (row) => {
    setEditingId(String(row.billing_profile_id));
    setForm({
      profile_code: row.profile_code || '',
      profile_name: row.profile_name || '',
      payer_type: row.payer_type || 'SELF',
      owner_entity_type: row.owner_entity_type || deriveDefaultOwnerType(row.payer_type || 'SELF'),
      owner_entity_id: row.owner_entity_id ? String(row.owner_entity_id) : '',
      billing_company_name: row.billing_company_name || '',
      address_line1: row.address_line1 || '',
      address_line2: row.address_line2 || '',
      city: row.city || '',
      state: row.state || '',
      country: row.country || 'India',
      pin_code: row.pin_code || '',
      gstin_number: row.gstin_number || '',
      phone: row.phone || '',
      email: row.email || '',
      website: row.website || '',
      logo_url: row.logo_url || '',
      is_default: row.is_default === true,
      is_active: row.is_active !== false
    });
    setError('');
    setMessage('');
    scrollToForm();
  };

  const deactivateProfile = async (row) => {
    if (!row?.billing_profile_id) return;
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/pms/billing-profiles/${row.billing_profile_id}`, {
        method: 'DELETE',
        headers: headers()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to deactivate billing profile');
      setMessage('Billing profile deactivated');
      if (String(editingId) === String(row.billing_profile_id)) resetProfileForm();
      loadProfiles();
    } catch (err) {
      setError(err?.message || 'Failed to deactivate billing profile');
    }
  };

  const saveInvoiceConfig = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    const tpl = String(invoiceConfig.template || '').trim();
    if (!tpl) return setError('Template is required');
    if (!tpl.toUpperCase().includes('{SEQ}')) return setError('Template must include {SEQ} token');
    const continueFromRaw = String(invoiceContinueFrom || '').trim();
    if (continueFromRaw && (!/^\d+$/.test(continueFromRaw))) {
      return setError('Continue From must be a whole number (e.g. 1042)');
    }
    setInvoiceSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        template: tpl,
        seq_padding: Number(invoiceConfig.seq_padding || 0),
        reset_cycle: String(invoiceConfig.reset_cycle || 'NEVER').toUpperCase(),
        is_active: invoiceConfig.is_active !== false
      };
      // "Continue From" is a one-time override (e.g. the property was
      // already numbering manually as INV-1042) — only sent when filled in,
      // and cleared after a successful save so it never silently re-applies.
      if (continueFromRaw) payload.current_seq = Number(continueFromRaw);
      const res = await fetch('/api/v1/pms/invoice-number-config', {
        method: 'PUT',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save invoice number config');
      setMessage(continueFromRaw
        ? `Invoice number config saved — the next invoice created will be sequence ${Number(continueFromRaw) + 1}`
        : 'Invoice number config saved');
      setInvoiceContinueFrom('');
      await loadInvoiceConfig();
    } catch (err) {
      setError(err?.message || 'Failed to save invoice number config');
    } finally {
      setInvoiceSaving(false);
    }
  };

  const saveReservationConfig = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    const tpl = String(reservationConfig.template || '').trim();
    if (!tpl) return setError('Template is required');
    if (!tpl.toUpperCase().includes('{SEQ}')) return setError('Template must include {SEQ} token');
    const continueFromRaw = String(reservationContinueFrom || '').trim();
    if (continueFromRaw && (!/^\d+$/.test(continueFromRaw))) {
      return setError('Continue From must be a whole number (e.g. 4308)');
    }
    setReservationSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        template: tpl,
        seq_padding: Number(reservationConfig.seq_padding || 0),
        reset_cycle: String(reservationConfig.reset_cycle || 'NEVER').toUpperCase(),
        is_active: reservationConfig.is_active !== false
      };
      // "Continue From" is a one-time override (e.g. the property was
      // already numbering manually as RES-4308) — only sent when filled in,
      // and cleared after a successful save so it never silently re-applies.
      if (continueFromRaw) payload.current_seq = Number(continueFromRaw);
      const res = await fetch('/api/v1/pms/reservation-number-config', {
        method: 'PUT',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save reservation number config');
      setMessage(continueFromRaw
        ? `Reservation number config saved — the next reservation created will be sequence ${Number(continueFromRaw) + 1}`
        : 'Reservation number config saved');
      setReservationContinueFrom('');
      await loadReservationConfig();
    } catch (err) {
      setError(err?.message || 'Failed to save reservation number config');
    } finally {
      setReservationSaving(false);
    }
  };

  return (
    <div className="page-shell">
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Front Office - PMS Billing Info</h3>
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      <div className="filter-bar" style={{ marginBottom: '0.8rem' }}>
        <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.45rem' }}>Invoice Number Configuration</div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '2fr 1fr 1fr 1fr', gap: '0.55rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
            Template
            <input
              value={invoiceConfig.template}
              onChange={(e) => setInvoiceConfig((p) => ({ ...p, template: e.target.value }))}
              placeholder="INV-{HOTELCODE}-{YYYY}{MM}-{SEQ}"
            />
            <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 400 }}>
              Tokens: {'{YYYY}'} {'{YY}'} {'{MM}'} {'{DD}'} {'{FY}'} {'{SEQ}'} {'{HOTELCODE}'} &mdash; {'{SEQ}'} is required. {'{FY}'} = Financial Year (e.g. 2526 for FY 2025-26)
            </span>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
            Seq Padding
            <input
              type="number"
              min={1}
              max={12}
              value={invoiceConfig.seq_padding}
              onChange={(e) => setInvoiceConfig((p) => ({ ...p, seq_padding: e.target.value }))}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
            Reset Cycle
            <select value={invoiceConfig.reset_cycle} onChange={(e) => setInvoiceConfig((p) => ({ ...p, reset_cycle: e.target.value }))}>
              <option value="NEVER">NEVER</option>
              <option value="YEARLY">YEARLY (Jan-Dec)</option>
              <option value="FINANCIAL_YEAR">FINANCIAL YEAR (Apr-Mar)</option>
              <option value="MONTHLY">MONTHLY</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
            Status
            <select value={invoiceConfig.is_active ? 'Y' : 'N'} onChange={(e) => setInvoiceConfig((p) => ({ ...p, is_active: e.target.value === 'Y' }))}>
              <option value="Y">Active</option>
              <option value="N">Inactive</option>
            </select>
          </label>
        </div>
        <div style={{ marginTop: '0.6rem', padding: '0.6rem', background: '#fffaf0', borderRadius: '6px', border: '1px dashed #f6ad55' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem', maxWidth: '280px' }}>
            Continue From (optional, one-time)
            <input
              type="text"
              inputMode="numeric"
              value={invoiceContinueFrom}
              onChange={(e) => setInvoiceContinueFrom(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="e.g. 1042"
            />
          </label>
          <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>
            Already numbering manually (e.g. last used was INV-1042)? Enter <b>1042</b> here and save — the next
            invoice generated will be <b>1043</b>. This affects live, currently-active invoice numbering — leave
            blank on every future save; only set it when deliberately re-baselining the sequence (e.g. moving
            this property onto a new template for the first time). Under GST rules, invoice numbers must stay
            strictly sequential with no gaps or repeats, so double-check this against the last invoice actually
            issued before saving.
          </span>
        </div>
        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '0.82rem', color: '#4a5568' }}>
            Current Sequence: <b>{Number(invoiceConfig.current_seq || 0)}</b>
            {invoiceConfig.last_reset_key ? <span> | Last Reset Key: <b>{invoiceConfig.last_reset_key}</b></span> : null}
          </div>
          <button onClick={saveInvoiceConfig} disabled={invoiceSaving} style={{ minWidth: '170px' }}>
            {invoiceSaving ? 'Saving...' : 'Save Invoice Config'}
          </button>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: '0.8rem' }}>
        <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.45rem' }}>Reservation Number Configuration</div>
        <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>
          {reservationConfig.is_configured
            ? 'This property uses its own reservation numbering below.'
            : 'Not configured — reservations still use the default RES-<id> numbering (a global counter shared across every property). Configure and save below to switch this property to its own hotel-specific sequence.'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '2fr 1fr 1fr 1fr', gap: '0.55rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
            Template
            <input
              value={reservationConfig.template}
              onChange={(e) => setReservationConfig((p) => ({ ...p, template: e.target.value }))}
              placeholder="RES-{HOTELCODE}-{SEQ}"
            />
            <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 400 }}>
              Tokens: {'{YYYY}'} {'{YY}'} {'{MM}'} {'{DD}'} {'{FY}'} {'{SEQ}'} {'{HOTELCODE}'} &mdash; {'{SEQ}'} is required.
            </span>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
            Seq Padding
            <input
              type="number"
              min={1}
              max={12}
              value={reservationConfig.seq_padding}
              onChange={(e) => setReservationConfig((p) => ({ ...p, seq_padding: e.target.value }))}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
            Reset Cycle
            <select value={reservationConfig.reset_cycle} onChange={(e) => setReservationConfig((p) => ({ ...p, reset_cycle: e.target.value }))}>
              <option value="NEVER">NEVER</option>
              <option value="YEARLY">YEARLY (Jan-Dec)</option>
              <option value="FINANCIAL_YEAR">FINANCIAL YEAR (Apr-Mar)</option>
              <option value="MONTHLY">MONTHLY</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem' }}>
            Status
            <select value={reservationConfig.is_active ? 'Y' : 'N'} onChange={(e) => setReservationConfig((p) => ({ ...p, is_active: e.target.value === 'Y' }))}>
              <option value="Y">Active</option>
              <option value="N">Inactive</option>
            </select>
          </label>
        </div>
        <div style={{ marginTop: '0.6rem', padding: '0.6rem', background: '#f7fafc', borderRadius: '6px', border: '1px dashed #cbd5e0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.84rem', maxWidth: '280px' }}>
            Continue From (optional, one-time)
            <input
              type="text"
              inputMode="numeric"
              value={reservationContinueFrom}
              onChange={(e) => setReservationContinueFrom(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="e.g. 4308"
            />
          </label>
          <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>
            Already numbering manually (e.g. last used was RES-4308)? Enter <b>4308</b> here and save — the next
            reservation created will be <b>4309</b>, continuing the same sequence instead of restarting at 1.
            Leave blank on every future save; this only needs to be set once. To match the old unpadded style
            exactly (RES-4309, not RES-04309), also set Seq Padding to 1.
          </span>
        </div>
        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '0.82rem', color: '#4a5568' }}>
            Current Sequence: <b>{Number(reservationConfig.current_seq || 0)}</b>
            {reservationConfig.last_reset_key ? <span> | Last Reset Key: <b>{reservationConfig.last_reset_key}</b></span> : null}
          </div>
          <button onClick={saveReservationConfig} disabled={reservationSaving} style={{ minWidth: '170px' }}>
            {reservationSaving ? 'Saving...' : 'Save Reservation Config'}
          </button>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: '0.8rem' }}>
        <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.45rem' }}>Billing Profile Filters</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '0.5rem' }}>
          <select value={filters.payer_type} onChange={(e) => setFilters((p) => ({ ...p, payer_type: e.target.value }))}>
            <option value="">All Payers</option>
            <option value="SELF">SELF</option>
            <option value="CORPORATE">CORPORATE</option>
            <option value="TRAVEL_AGENT">TRAVEL_AGENT</option>
          </select>
          <select value={filters.owner_entity_type} onChange={(e) => setFilters((p) => ({ ...p, owner_entity_type: e.target.value }))}>
            <option value="">All Owners</option>
            <option value="PROPERTY">PROPERTY</option>
            <option value="CORPORATE">CORPORATE</option>
            <option value="TRAVEL_AGENT">TRAVEL_AGENT</option>
            <option value="GUEST">GUEST</option>
          </select>
          <input
            placeholder="Owner Entity ID"
            value={filters.owner_entity_id}
            onChange={(e) => setFilters((p) => ({ ...p, owner_entity_id: e.target.value }))}
          />
          <select value={filters.include_inactive ? 'Y' : 'N'} onChange={(e) => setFilters((p) => ({ ...p, include_inactive: e.target.value === 'Y' }))}>
            <option value="Y">Include Inactive</option>
            <option value="N">Only Active</option>
          </select>
        </div>
        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => loadProfiles(filters)}>Apply Filter</button>
          <button onClick={() => { setFilters(emptyFilters); loadProfiles(emptyFilters); }}>Reset Filter</button>
        </div>
      </div>

      <div ref={formRef} className="filter-bar" style={{ marginBottom: '0.8rem', ...editHighlightStyle(editHighlight) }}>
        <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.45rem' }}>
          {editingId ? 'Edit Billing Profile' : 'Create Billing Profile'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.5rem' }}>
          <input placeholder="Profile Code *" value={form.profile_code} onChange={(e) => setForm((p) => ({ ...p, profile_code: e.target.value }))} />
          <input placeholder="Profile Name *" value={form.profile_name} onChange={(e) => setForm((p) => ({ ...p, profile_name: e.target.value }))} />
          <select
            value={form.payer_type}
            onChange={(e) => {
              const payerType = e.target.value;
              const ownerType = deriveDefaultOwnerType(payerType);
              setForm((p) => ({ ...p, payer_type: payerType, owner_entity_type: ownerType, owner_entity_id: ownerType === 'PROPERTY' ? '' : p.owner_entity_id }));
            }}
          >
            <option value="SELF">SELF</option>
            <option value="CORPORATE">CORPORATE</option>
            <option value="TRAVEL_AGENT">TRAVEL_AGENT</option>
          </select>
          <select value={form.owner_entity_type} onChange={(e) => setForm((p) => ({ ...p, owner_entity_type: e.target.value, owner_entity_id: e.target.value === 'PROPERTY' ? '' : p.owner_entity_id }))}>
            <option value="PROPERTY">PROPERTY</option>
            <option value="CORPORATE">CORPORATE</option>
            <option value="TRAVEL_AGENT">TRAVEL_AGENT</option>
            <option value="GUEST">GUEST</option>
          </select>
          {String(form.owner_entity_type || '').toUpperCase() === 'PROPERTY' ? (
            <input value="Property-level default" disabled />
          ) : ownerOptions.length ? (
            <select value={form.owner_entity_id} onChange={(e) => setForm((p) => ({ ...p, owner_entity_id: e.target.value }))}>
              <option value="">Select Owner *</option>
              {ownerOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          ) : (
            <input
              placeholder="Owner Entity ID *"
              value={form.owner_entity_id}
              onChange={(e) => setForm((p) => ({ ...p, owner_entity_id: e.target.value }))}
            />
          )}
          <input placeholder="Billing Company Name" value={form.billing_company_name} onChange={(e) => setForm((p) => ({ ...p, billing_company_name: e.target.value }))} />
          <input placeholder="GSTIN" value={form.gstin_number} onChange={(e) => setForm((p) => ({ ...p, gstin_number: e.target.value }))} />
          <input placeholder="Address Line 1" value={form.address_line1} onChange={(e) => setForm((p) => ({ ...p, address_line1: e.target.value }))} />
          <input placeholder="Address Line 2" value={form.address_line2} onChange={(e) => setForm((p) => ({ ...p, address_line2: e.target.value }))} />
          <input placeholder="City" value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} />
          <input placeholder="State" value={form.state} onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))} />
          <input placeholder="Country" value={form.country} onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))} />
          <input placeholder="PIN Code" value={form.pin_code} onChange={(e) => setForm((p) => ({ ...p, pin_code: e.target.value }))} />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
          <input placeholder="Email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
          <input placeholder="Website" value={form.website} onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))} />
          <select value={form.is_default ? 'Y' : 'N'} onChange={(e) => setForm((p) => ({ ...p, is_default: e.target.value === 'Y' }))}>
            <option value="N">Not Default</option>
            <option value="Y">Set Default</option>
          </select>
          <select value={form.is_active ? 'Y' : 'N'} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.value === 'Y' }))}>
            <option value="Y">Active</option>
            <option value="N">Inactive</option>
          </select>
        </div>
        {/* Logo Upload */}
        <div style={{ marginTop: '0.55rem', display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 600, fontSize: '0.84rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            Profile Logo
            <input type="file" accept="image/*" onChange={handleLogoChange} style={{ fontSize: '0.82rem' }} />
            <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 400 }}>Max 2 MB. Will appear on invoices for this billing profile.</span>
          </label>
          {form.logo_url ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <img
                src={form.logo_url.startsWith('data:') || form.logo_url.startsWith('http') ? form.logo_url : window.location.origin + form.logo_url}
                alt="Logo preview"
                style={{ height: 48, maxWidth: 160, objectFit: 'contain', border: '1px solid var(--theme-border)', borderRadius: 4, background: '#fff', padding: 2 }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, logo_url: '' }))}
                style={{ background: '#fff5f5', color: '#c53030', border: '1px solid #fed7d7', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem', padding: '0.2rem 0.5rem' }}
              >
                Remove
              </button>
            </div>
          ) : null}
        </div>
        <div style={{ marginTop: '0.55rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={saveProfile}>{editingId ? 'Update Profile' : 'Add Profile'}</button>
          <button onClick={resetProfileForm}>Clear</button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Logo</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Code</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Profile</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Payer</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Owner</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Billing Company</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>GSTIN</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Default</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.billing_profile_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                <td style={{ padding: '0.45rem' }}>
                  {row.logo_url ? (
                    <img
                      src={row.logo_url.startsWith('http') ? row.logo_url : window.location.origin + row.logo_url}
                      alt=""
                      style={{ height: 28, maxWidth: 60, objectFit: 'contain' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : <span style={{ color: '#a0aec0', fontSize: '0.75rem' }}>-</span>}
                </td>
                <td style={{ padding: '0.45rem' }}>{row.profile_code}</td>
                <td style={{ padding: '0.45rem' }}>{row.profile_name}</td>
                <td style={{ padding: '0.45rem' }}>{row.payer_type}</td>
                <td style={{ padding: '0.45rem' }}>
                  {row.owner_entity_type}
                  {row.owner_entity_id ? ` #${row.owner_entity_id}` : ''}
                </td>
                <td style={{ padding: '0.45rem' }}>{row.billing_company_name || '-'}</td>
                <td style={{ padding: '0.45rem' }}>{row.gstin_number || '-'}</td>
                <td style={{ padding: '0.45rem', color: row.is_default ? '#276749' : '#718096' }}>{row.is_default ? 'YES' : 'NO'}</td>
                <td style={{ padding: '0.45rem', color: row.is_active ? '#276749' : '#c53030' }}>{row.is_active ? 'ACTIVE' : 'INACTIVE'}</td>
                <td style={{ padding: '0.45rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <button onClick={() => onEditProfile(row)}>Edit</button>
                  {row.is_active !== false ? (
                    <button
                      onClick={() => deactivateProfile(row)}
                      style={{ background: '#fff5f5', color: '#c53030', border: '1px solid #fed7d7', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      Deactivate
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {!rows.length && !loading ? (
              <tr><td colSpan={10} style={{ padding: '0.6rem', color: '#718096' }}>No billing profiles found</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PmsBillingInfoMaster;
